import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLeaveApplicationEntry } from './hooks/useLeaveApplicationEntry';
import { TYPE_OPTIONS } from './api/leaveApplicationApi';
import AppDropdown from '../../../components/AppDropdown';
import AppCombobox from '../../../components/AppCombobox';
import { fetchEmployeeOptions } from '../../../api/filterOptionApi';
import { appToast } from '../../../components/AppToast';
import LeaveBalanceCard from './components/LeaveBalanceCard';

export default function LeaveApplicationEntryScreen() {
  const { state, actions } = useLeaveApplicationEntry();
  const navigate = useNavigate();
  const {
    form,
    balances,
    employeeOptions,
    leaveTypeOptions,
    reasonOptions,
    isEdit,
    isLoading,
    isSaving,
  } = state;

  if (isLoading) {
    return (
      <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center p-16">
        <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
        <p className="mt-3 text-xs font-semibold text-on-surface-variant">Loading Application Details...</p>
      </div>
    );
  }

  const selectedEmployee = employeeOptions.find((e) => String(e.value) === String(form.emp_id));

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header Banner */}
      <section className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between border-b border-outline-variant/10 pb-4">
        <div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Leave Application' : 'Create Leave Application'}
          </h1>
        </div>

        <Link
          to="/payroll/leave-application"
          className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface-variant hover:text-primary hover:bg-surface-container-low transition shadow-xs w-fit"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          <span>Back to List</span>
        </Link>
      </section>

      {/* 2-Column Layout matching Screenshot 3 */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Form Card (lg:col-span-8) */}
        <div className="lg:col-span-8 rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm overflow-hidden flex flex-col justify-between">
          {/* Card Header */}
          <div className="border-b border-outline-variant/20 bg-surface-container-low/40 px-8 py-4">
            <h2 className="font-display text-lg font-bold text-on-surface">Leave Application</h2>
          </div>

          {/* Form Body matching Screenshot 3 */}
          <div className="p-8">
            <div className="max-w-xl mx-auto space-y-4">
              {isEdit && form.status === 'Approved' && (
                <div className="flex items-center gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs font-bold text-emerald-700 dark:text-emerald-300">
                  <span className="material-symbols-outlined text-lg">verified</span>
                  <span>This leave application has already been approved and is locked for editing.</span>
                </div>
              )}

              {/* Apply Date */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Apply Date <span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  value={form.applyDate}
                  disabled={isEdit && form.status === 'Approved'}
                  onChange={(e) => actions.patchForm('applyDate', e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>

              {/* Employee Combobox */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Employee <span className="text-error">*</span>
                </label>
                <div className="flex items-center gap-1.5 w-full">
                  <div className="flex-1 min-w-0">
                    <AppCombobox
                      fetchOptions={fetchEmployeeOptions}
                      value={form.emp_id ? Number(form.emp_id) : ''}
                      initialLabel={form.emp_name || ''}
                      disabled={isEdit && form.status === 'Approved'}
                      onChange={(val, opt) => {
                        actions.patchForm('emp_id', val ? String(val) : '');
                        if (opt?.label) {
                          actions.patchForm('emp_name', opt.label);
                        }
                      }}
                      placeholder="Select Employee..."
                      searchPlaceholder="Search employee name or code..."
                      triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => appToast.info('Employee account heads are linked to your Chart of Accounts under Account Group Employees.')}
                    aria-label="View Employee info"
                    title="Employee Account Head Info"
                    className="h-9 w-9 rounded-lg border border-outline-variant/30 bg-surface-container-low hover:bg-surface-container-high text-on-surface-variant flex items-center justify-center transition cursor-pointer shrink-0"
                  >
                    <span className="material-symbols-outlined text-base">info</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/masters/acheads/heads/entry?group=Employees')}
                    aria-label="Create new Employee Account Head"
                    title="Create new Account Head"
                    className="h-9 w-9 rounded-lg border border-primary/30 bg-primary/10 hover:bg-primary/20 text-primary flex items-center justify-center transition cursor-pointer shrink-0 font-bold"
                  >
                    <span className="material-symbols-outlined text-base">add</span>
                  </button>
                </div>
              </div>

              {/* From Date */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  From Date <span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  value={form.fromDate}
                  disabled={isEdit && form.status === 'Approved'}
                  onChange={(e) => actions.patchForm('fromDate', e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>

              {/* Till Date */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Till Date <span className="text-error">*</span>
                </label>
                <input
                  type="date"
                  value={form.tillDate}
                  disabled={isEdit && form.status === 'Approved'}
                  onChange={(e) => actions.patchForm('tillDate', e.target.value)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>

              {/* Type (FullDay, First Half, Second Half) */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Type <span className="text-error">*</span>
                </label>
                <AppDropdown
                  options={TYPE_OPTIONS}
                  value={form.type}
                  disabled={isEdit && form.status === 'Approved'}
                  onChange={(val) => actions.patchForm('type', val)}
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Leave Type */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Leave Type <span className="text-error">*</span>
                </label>
                <AppDropdown
                  options={leaveTypeOptions}
                  value={String(form.leaveTypeId || '')}
                  disabled={isEdit && form.status === 'Approved'}
                  onChange={(val) => actions.patchForm('leaveTypeId', val)}
                  placeholder="Select Leave Type"
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Days / Duration */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Days
                </label>
                <input
                  type="number"
                  min={0.5}
                  step={0.5}
                  value={form.days}
                  disabled={isEdit && form.status === 'Approved'}
                  onChange={(e) => actions.patchForm('days', parseFloat(e.target.value) || 0.5)}
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>

              {/* Reason */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Reason
                </label>
                <AppDropdown
                  options={reasonOptions}
                  value={String(form.reasonId || '')}
                  disabled={isEdit && form.status === 'Approved'}
                  onChange={(val) => actions.patchForm('reasonId', val)}
                  placeholder="Select Reason"
                  triggerClassName="h-9 w-full bg-surface-container-low text-xs border border-outline-variant/30 rounded-lg px-3"
                />
              </div>

              {/* Remarks */}
              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] items-center gap-3">
                <label className="text-xs font-bold text-on-surface-variant sm:text-right">
                  Remarks
                </label>
                <input
                  type="text"
                  value={form.remarks}
                  disabled={isEdit && form.status === 'Approved'}
                  onChange={(e) => actions.patchForm('remarks', e.target.value)}
                  placeholder="Additional remarks or handover details"
                  className="h-9 w-full rounded-lg border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20 disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons Row matching Screenshot 3 */}
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-outline-variant/15 bg-surface-container-low/40 px-8 py-4">
            <button
              type="button"
              disabled={isSaving || (isEdit && form.status === 'Approved')}
              onClick={() => actions.handleSave({ exitAfter: true })}
              className="h-9 px-6 bg-[#0047ab] hover:bg-[#003788] text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span className="material-symbols-outlined text-sm">save</span>
              <span>{isSaving ? 'Saving...' : 'save & eXit'}</span>
            </button>

            {!isEdit && (
              <button
                type="button"
                disabled={isSaving}
                onClick={() => actions.handleSave({ exitAfter: false })}
                className="h-9 px-5 bg-surface-container-lowest hover:bg-surface-container-low text-primary font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/40 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Save &amp; continue
              </button>
            )}

            {form.status === 'Pending' && (
              <button
                type="button"
                disabled={isSaving}
                onClick={actions.handleApproveAndExit}
                className="h-9 px-5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs transition shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-sm">check_circle</span>
                <span>Approve &amp; Exit</span>
              </button>
            )}

            <button
              type="button"
              onClick={() => navigate('/payroll/leave-application')}
              className="h-9 px-5 bg-surface-container-high hover:bg-surface-container-highest text-on-surface font-bold rounded-xl text-xs transition cursor-pointer border border-outline-variant/20"
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Right Written Context Panel & Leave Balances (lg:col-span-4) matching Screenshot 3 */}
        <div className="lg:col-span-4 space-y-4">
          {/* Live Leave Balance Card */}
          <LeaveBalanceCard
            balances={balances}
            employeeName={form.emp_name || selectedEmployee?.label}
          />

          {/* Tips / Workflow Panel */}
          <div className="rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-6 shadow-xs space-y-4">
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-wider text-on-surface">
              <span className="material-symbols-outlined text-base text-amber-500 font-bold">
                lightbulb
              </span>
              <span>TIPS:</span>
            </div>

            <p className="text-xs text-on-surface-variant leading-relaxed font-medium">
              Submit employee leave requests with automatic duration computation, leave quota validations, and instant balance updates.
            </p>

            <div className="pt-3 border-t border-outline-variant/15 space-y-2">
              <h4 className="text-[11px] font-bold text-on-surface uppercase tracking-wider">
                POLICY VALIDATIONS:
              </h4>
              <ul className="text-xs text-on-surface-variant space-y-2 list-disc list-inside leading-relaxed">
                <li>Leave durations must respect the minimum and maximum day limits set in the employee&apos;s assigned Leave Rule.</li>
                <li>Half-day leaves automatically assign 0.50 days.</li>
                <li>Approved leave records cannot be edited or deleted unless rejected or reset.</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
